import SalaryCard from "../components/hris/SalaryCard";
import { useEffect, useState } from "react";
import { api, idr } from "../lib/api";
import { Save, ReceiptText, Layers } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import InputRupiah from "../components/InputRupiah";
import { MonthPicker } from "@/components/ui/date-picker";
import { bulanIniISO } from "@/lib/tanggal";

export default function Payroll() {
  const { can } = useAuth();
  // Kop pratinjau slip = kop PDF slip di backend (payroll.py), dari pengaturan
  // Branding (keputusan owner 24 Sep 2026, white-label).

  const canSave = can("payroll", "create");
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState("");
  const [form, setForm] = useState({
    periode: bulanIniISO(),
    insentif_target: 0, bonus_kpi: 0, bonus_performa: 0,
    lembur_jam: 0, menit_telat: 0, hari_alpha: 0, menit_pulang_awal: 0,
    kasbon: 0, potongan_lain: 0,
  });
  const [preview, setPreview] = useState(null);
  const [useAttendance, setUseAttendance] = useState(false);
  const [useFromPoints, setUseFromPoints] = useState(false);

  useEffect(() => { api.get("/employees").then(r => setEmployees(r.data)); }, []);

  const doPreview = async () => {
    if (!selected) { toast.error("Pilih karyawan"); return; }
    try {
      const { data } = await api.post("/payroll/preview", { employee_id: selected, ...form, auto_from_attendance: useAttendance, insentif_from_points: useFromPoints });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const doSave = async () => {
    try {
      await api.post("/payroll/save", { employee_id: selected, ...form, auto_from_attendance: useAttendance, insentif_from_points: useFromPoints });
      toast.success("Slip gaji tersimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const upd = (k, v) => setForm(s => ({ ...s, [k]: typeof v === "number" ? v : parseFloat(v) || 0 }));

  const doBulk = async () => {
    if (!confirm(`Buat slip default untuk semua karyawan aktif periode ${form.periode}? Karyawan yang sudah punya slip di periode ini akan di-skip.`)) return;
    try {
      const { data } = await api.post("/payroll/bulk", { periode: form.periode });
      toast.success(`Bulk selesai: ${data.created} slip dibuat, ${data.skipped} di-skip dari ${data.total} karyawan`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Rincian insentif berjenjang (brief bos #26) — hanya terisi kalau slip
  // dihitung dengan "insentif otomatis dari poin" aktif (routers/payroll.py
  // _terapkan_tier_insentif). Slip lama/manual tidak punya field ini, jadi
  // tampilan lama (Row flat) tetap dipertahankan lewat pengecekan `ps?.insentif_cair`.
  const ps = preview?.calc?.points_summary || null;

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-8 no-print">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Payroll</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Generator Slip Gaji</h1>
        <p className="mt-2 text-sm text-slate-500">Pilih karyawan lalu isi variabel gaji bulanan untuk menghasilkan slip.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm no-print">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Input Data</h3>
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Pilih Karyawan *</span>
              <select value={selected} onChange={e => setSelected(e.target.value)} data-testid="payroll-select-employee"
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                <option value="">— pilih karyawan —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.nama} — {e.grade_jabatan}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Periode</span>
              <MonthPicker value={form.periode} onChange={v => setForm(s => ({ ...s, periode: v }))}
                className="mt-1" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["insentif_target","Insentif Target"],["bonus_kpi","Bonus KPI"],
                ["bonus_performa","Bonus Performa"],["lembur_jam","Lembur (jam)"],
                ["menit_telat","Menit Telat"],["hari_alpha","Hari Alpha"],
                ["menit_pulang_awal","Menit Pulang Awal"],
                ["kasbon","Kasbon"],["potongan_lain","Potongan Lain"],
              ].map(([k, l]) => {
                const isUang = ["insentif_target", "bonus_kpi", "bonus_performa", "kasbon", "potongan_lain"].includes(k);
                return (
                  <label key={k} className="block">
                    <span className="text-xs font-medium text-slate-600">{l}</span>
                    {isUang ? (
                      <InputRupiah value={form[k]} onChange={n => upd(k, n)} testid={`payroll-${k}`}
                        className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                    ) : (
                      <input type="number" value={form[k]} onChange={e => upd(k, e.target.value)} data-testid={`payroll-${k}`}
                        className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                    )}
                  </label>
                );
              })}
            </div>

            {/* Auto toggles */}
            <div className="border border-emerald-100 bg-emerald-50/40 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Otomasi Perhitungan</p>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={useAttendance} onChange={e => setUseAttendance(e.target.checked)}
                  data-testid="payroll-auto-attendance" className="w-4 h-4 accent-emerald-600" />
                <span className="text-slate-700">Auto dari absensi <span className="text-slate-500 text-xs">(hitung menit telat & hari alpha dari data attendance)</span></span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={useFromPoints} onChange={e => setUseFromPoints(e.target.checked)}
                  data-testid="payroll-from-points" className="w-4 h-4 accent-sky-600" />
                <span className="text-slate-700">Insentif otomatis dari poin sales <span className="text-slate-500 text-xs">(total poin × bobot per poin dari data karyawan)</span></span>
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={doPreview} data-testid="btn-preview-payroll"
                className="flex-1 h-10 rounded-lg bg-[#0F172A] hover:bg-slate-800 text-white text-sm font-medium transition-colors">
                Hitung Preview
              </button>
              {canSave && (
                <button onClick={doSave} disabled={!preview} data-testid="btn-save-payroll"
                  className="h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                  <Save className="w-4 h-4" /> Simpan
                </button>
              )}
            </div>

            {/* Generate massal: fungsinya (doBulk) dan endpointnya sudah ada sejak
                awal, tapi tidak pernah tersambung ke elemen mana pun — ikon Layers
                bahkan sudah diimpor. Akibatnya fitur ini tidak bisa dipakai siapa
                pun. Dipisah dari baris tombol di atas karena cakupannya beda:
                yang di atas satu karyawan, yang ini SEMUA karyawan aktif. */}
            {canSave && (
              <div className="border-t border-slate-200 pt-3">
                <button onClick={doBulk} data-testid="btn-bulk-payroll"
                  className="w-full h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium inline-flex items-center justify-center gap-2">
                  <Layers className="w-4 h-4" /> Generate Massal — semua karyawan aktif
                </button>
                <p className="mt-1.5 text-xs text-slate-500">
                  Membuat slip default periode {form.periode} untuk semua karyawan aktif.
                  Yang sudah punya slip di periode ini dilewati, tidak ditimpa.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border-2 border-slate-200 rounded-xl p-8 print-area shadow-sm min-h-[400px]">
          {!preview ? (
            <div className="h-full grid place-items-center text-center py-16">
              <div>
                <ReceiptText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Preview slip gaji akan tampil di sini</p>
              </div>
            </div>
          ) : (
            <>
              <SalaryCard preview slip={{ employee_nama: preview.employee.nama, grade_jabatan: preview.employee.grade_jabatan, branch: preview.employee.branch, periode: preview.periode, calc: preview.calc }} />
              <span className="sr-only" data-testid="payroll-thp">{idr(preview.calc.thp)}</span>
              {ps && <details className="mt-4 text-xs text-slate-500"><summary className="cursor-pointer">Perhitungan poin & insentif</summary><div className="mt-2 space-y-1">
                {ps.insentif_cair !== undefined && <p data-testid="payroll-insentif-tier">Realisasi {ps.pct_realisasi}% · Tier cair {ps.tier_cair_pct}% · Insentif {idr(ps.insentif_cair)} dari dasar {idr(ps.insentif_dasar)}</p>}
                {[['sale_hp','HP'],['sale_acc','Aksesori'],['acq_hp','Akuisisi HP'],['acq_device','Akuisisi device']].map(([key,label])=>ps.breakdown?.[key] && <p key={key}>{label}: {ps.breakdown[key].n} × {ps.breakdown[key].rule} = {ps.breakdown[key].poin} poin</p>)}
                {ps.breakdown?.per_gudang && Object.keys(ps.breakdown.per_gudang).length > 0 && <div data-testid="payroll-per-gudang">{Object.values(ps.breakdown.per_gudang).map((g,i)=><p key={i}>{g.warehouse_nama}: {g.n} unit = {g.poin} poin</p>)}</div>}
                <p>Total: {ps.total_poin} poin × {idr(ps.bobot_per_poin)} = {idr(ps.insentif_rupiah)}</p>
                {ps.target_poin > 0 && <p>Pencapaian: {ps.total_poin} / {ps.target_poin} ({ps.realisasi_pct}%)</p>}
              </div></details>}
              {preview.calc.hari_kerja_kurang > 0 && <p className="mt-2 text-xs text-amber-700" data-testid="payroll-short-shift-note">{preview.calc.hari_kerja_kurang} hari di bawah ambang jam kerja dihitung tidak hadir.</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
